import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import { PublicInfoCard } from "Components/Social/PublicInfoCard";
import { ProfileVisibilitySwitch } from "Components/Social/ProfileVisibilitySwitch";
import { PROFILE_VISIBILITY_SWITCH_ID } from "Components/Social/ProfileVisibilityToggle";
import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  IdentityVerificationStatus,
  type PartyDto,
  PartyType,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.PublicPermissions;

type BadgeVariant = "full" | "pending";

/**
 * The badge is cosmetic — it previews the credibility marker shown next to the user
 * wherever their identity appears publicly (once they enable identity visibility):
 * muted while KYC verification is still pending, full once it passed. No badge when
 * verification hasn't started or has expired.
 */
const BADGE_BY_STATUS: Record<IdentityVerificationStatus, BadgeVariant | null> =
  {
    [IdentityVerificationStatus.Verified]: "full",
    [IdentityVerificationStatus.Processing]: "pending",
    [IdentityVerificationStatus.ToVerify]: "pending",
    [IdentityVerificationStatus.NotVerified]: null,
    [IdentityVerificationStatus.Expired]: null,
  };

type Props = {
  party: PartyDto;
};

/**
 * Identity card of the public-information tab (ZS-1320): the person's first name +
 * surname (KYC-owned — no edit) with the verification badge preview next to the name.
 * Customer personas get the public/private profile switch in the footer; business
 * personas (always public) get none. The name comes from the logged-in user, NOT from
 * the tab's party — except for LegalEntity merchants/hunters, whose party is the
 * company: there the company name is the persona's identity (the login carries no
 * person name for signup-origin business accounts, which would render as "-").
 */
export const IdentityCard = ({ party }: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const { data: verification } = useVerificationsQuery();
  const isHunter = useIsCurrentUserHunter();
  const isMerchant = useIsCurrentUserMerchant();

  // The tab only exists for Customer/Hunter/Merchant details — non-business means Customer.
  const isBusinessPersona = isHunter || isMerchant;

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const displayName =
    party.type === PartyType.LegalEntity
      ? party.companyName || fullName
      : fullName;
  const badgeVariant = verification
    ? BADGE_BY_STATUS[verification.identityVerificationStatus]
    : null;

  return (
    <PublicInfoCard
      label={t(RESOURCES.IdentityLabel)}
      value={displayName || null}
      footer={
        isBusinessPersona ? undefined : (
          <>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography
                component="label"
                htmlFor={PROFILE_VISIBILITY_SWITCH_ID}
                variant="caption"
                color="text.secondary"
                sx={{ cursor: "pointer" }}
              >
                {t(RESOURCES.PublicProfileLabel)}
              </Typography>
              <Tooltip title={t(RESOURCES.SectionDescription)}>
                <IconButton size="small" sx={{ p: 0.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} color="disabled" />
                </IconButton>
              </Tooltip>
            </Stack>
            <ProfileVisibilitySwitch />
          </>
        )
      }
      valueAdornment={
        <>
          {badgeVariant === "full" && (
            <Tooltip title={t(RESOURCES.IdentityVerifiedTooltip)}>
              <VerifiedIcon color="success" fontSize="small" />
            </Tooltip>
          )}
          {badgeVariant === "pending" && (
            <Tooltip title={t(RESOURCES.IdentityVerificationPendingTooltip)}>
              <VerifiedOutlinedIcon color="disabled" fontSize="small" />
            </Tooltip>
          )}
        </>
      }
    />
  );
};
